/**
 * Shared checker rule helpers (W2-12b checker-rules; DESIGN.md §5.10, §7.6; ARCHITECTURE.md §3.2).
 *
 * Rule files (`src/features/checker/rules/*.rules.ts`) may import only `src/core/**` and `src/formats/**` (they run
 * in the analysis worker), so everything two rule files share lives here:
 *
 *  - problem ids and emission: `problemId` builds the stable `${rule}:${entityKind}:${id}` id, which is also the
 *    "Ignore here" key stored in `ChartDoc.editor.ignoredProblems` (Core.doc.ignoreProblem); `emitProblem` drops
 *    ignored problems;
 *  - fix references: the `problems.fix.*` command ids the rules reference (`FIX_COMMANDS`, argument shapes in
 *    `FixArgs`, registered by W2-12a's fixes.ts; change request docs/design/change-requests/W2-12b.md) and the
 *    navigation commands of `CommandArgs` they use (`NavArgs`);
 *  - message parameters: beats, seconds, milliseconds, line labels, easings (select keys plus RPE identifiers, so
 *    the catalogue translates the wording), inspector field paths (`FIELD`);
 *  - geometry (rendering.md R§1, R§3.1, R§4.3; rpe-format.md F§10.1, F§11.4, F§11.6): the effective aspect ratio of
 *    the reference viewport, line frames at a time, the JUDGE point of a note (its raw positionX on the line: Phira
 *    judges by the x distance along the note's line and ignores incline and posControl), the judge distance between
 *    a touch and a note of another line, screen bounds, whether a note's judge band crosses the screen, read time
 *    (`Engine.noteAppearSec`);
 *  - a per-revision table of notes sorted by hit time, shared by the gameplay rules;
 *  - media decodability (formats-and-packaging.md P§2.7), package path hygiene (P§2.1);
 *  - easing helpers (overshoot range, safe replacement), Phira's string tween cases (F§9.3) and Rust's f32 parser;
 *  - extra.json shader uniforms (P§5.2: built-in uniform table, default comments of custom shaders);
 *  - Phira's Bezier registry scan with event provenance (F§6.7) for cache-key collisions.
 *
 * Pure, no DOM. Coordinates: world = Phira chart space (x in [-1, 1], y in [-1/ar, 1/ar], y up); RPE px:
 * X = x * 675, Y = y * ar * 450.
 */

import { Ease as EaseApi } from '../ease/index';
import { catalogEntry, catalogVariant } from '../ease/catalog';
import type { BezierPoints, Ease, EaseFamily } from '../ease/types';
import {
  EXTENDED_BEZIER_SCAN,
  LAYER_BEZIER_SCAN,
  extendedOf,
  ignoredList,
  rawEventBezier,
} from '../engine/bezierRegistry';
import type { Engine } from '../engine/types';
import { computeViewport } from '../engine/viewport';
import type { IrLine } from '../ir/types';
import { lineKind } from '../model/lineKind';
import type {
  AssetRef,
  BuiltinShader,
  ChartDoc,
  ControlCurve,
  DocIndexes,
  EffectVarType,
  Key,
  KeyId,
  LineId,
  LineKind,
  LineProp,
  Note,
  NoteId,
  NoteKind,
  Track,
  TrackId,
  TrackValue,
} from '../model/types';
import { formatBeat, formatSec } from '../time/beatSyntax';
import type { Beat, Sec } from '../time/types';
import type { Json, MessageRef } from '../util/types';
import type { CheckContext, CheckRule, CheckThresholds, FixRef, ProblemDraft, ProblemLocation } from './types';

const fr = Math.fround;

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

/** RPE Auto-Correct parameters (AC-04) plus ours: the defaults of Settings > Checker (DESIGN.md §7.6). */
export const CHECK_THRESHOLD_DEFAULTS: CheckThresholds = Object.freeze({
  xMax: 700,
  collisionPx: 200,
  readTimeSec: 0.3,
  dragWindowSec: 0.04,
  comboWindowSec: 0.16,
  nearSimultaneousSec: 0.002,
});

/** Reference viewport of the geometry rules (DESIGN.md §7.6 GP-06). */
export const REFERENCE_VIEWPORT: { readonly w: number; readonly h: number } = Object.freeze({ w: 1920, h: 1080 });

/** Phira draws (and the notes module probes visibility) on a 60 Hz frame grid. */
export const PHIRA_FRAME_SEC = 1 / 60;

/** RPE x px per world unit (x = positionX / 675). */
export const RPE_X_PER_WORLD = 675;

/** RPE y px per world unit times the aspect ratio (y = moveY / 450 / ar). */
export const RPE_Y_PER_WORLD_AR = 450;

/** Two times closer than this (seconds) are the same instant (exact beats give bit-identical seconds). */
export const SAME_TIME_EPS_SEC = 1e-6;

/** Tolerance of the inclusive time-window comparisons (beats converted to seconds are not exact decimals). */
export const WINDOW_EPS_SEC = 1e-9;

/** Phira refuses a song-select preview that does not end this long before the music ends (DESIGN.md PH-36). */
export const PREVIEW_END_MARGIN_SEC = 0.5;

/** Phira's load-time preview check uses `previewStart + 1` when previewEnd is not set. */
export const PREVIEW_NULL_END_SEC = 1;

/** Line images larger than this on either side shimmer when shrunk (no mipmaps in Phira gameplay, PH-27). */
export const MAX_TEXTURE_SIDE_PX = 2048;

/** RPE's editor shows this many event layers (PH-25). */
export const RPE_VISIBLE_LAYERS = 4;

/** Note `hitsound` strings Phira maps to its built-in sounds before looking in the package (F§11.1, PH-38). */
export const RESERVED_HITSOUND_NAMES: readonly string[] = Object.freeze(['tap.mp3', 'flick.mp3', 'drag.mp3']);

/** Phira's inert-control tolerance (F§12): first value within 1e-4 of 1. */
const CONTROL_INERT_EPS = fr(1e-4);

// ---------------------------------------------------------------------------------------------------------------
// Problem ids, ignore keys, emission
// ---------------------------------------------------------------------------------------------------------------

/** What a problem is about; the middle part of its id and of its "Ignore here" key. */
export type EntityKind =
  | 'note'
  | 'pair'
  | 'line'
  | 'track'
  | 'key'
  | 'asset'
  | 'effect'
  | 'video'
  | 'chart'
  | 'curve'
  | 'span'
  | 'control'
  | 'ui';

/**
 * Stable problem id `${rule}:${entityKind}:${id}`. It is ALSO the suppression key that "Ignore here" stores in
 * `ChartDoc.editor.ignoredProblems` (`${ruleId}:${entityKind}:${id}`), so ignoring and diffing use one string.
 */
export function problemId(rule: string, kind: EntityKind, id: string | number): string {
  return `${rule}:${kind}:${String(id)}`;
}

/** Order-independent id of a pair of numeric entities ("12-15"). */
export function pairKey(a: number, b: number): string {
  return a <= b ? `${String(a)}-${String(b)}` : `${String(b)}-${String(a)}`;
}

const ignoredSets = new WeakMap<readonly string[], ReadonlySet<string>>();

/** Whether the document suppresses this problem id ("Ignore here"). */
export function isIgnored(doc: Pick<ChartDoc, 'editor'>, id: string): boolean {
  const list = doc.editor.ignoredProblems;
  if (list.length === 0) return false;
  let set = ignoredSets.get(list);
  if (set === undefined) {
    set = new Set(list);
    ignoredSets.set(list, set);
  }
  return set.has(id);
}

/**
 * Whether a fix only does what "Go" already does for the problem (UX-093): `nav.reveal` to the problem's own beat,
 * line, notes and keys. The Problems panel and the Publish summary both offer "Go" next to the fixes, so such a fix
 * would be a second button with the same effect ("Go" and "Go to" side by side).
 */
export function duplicatesGo(fix: FixRef, at: ProblemLocation): boolean {
  if (fix.command !== 'nav.reveal' || at.beat === undefined) return false;
  const a = (fix.args ?? null) as {
    readonly beat?: { readonly w: number; readonly n: number; readonly d: number };
    readonly line?: number;
    readonly notes?: readonly number[];
    readonly keys?: readonly number[];
  } | null;
  if (a === null || typeof a !== 'object' || a.beat === undefined) return false;
  const sameList = (x: readonly number[] | undefined, y: readonly number[] | undefined): boolean => {
    const xs = x ?? [];
    const ys = y ?? [];
    return xs.length === ys.length && xs.every((v, i) => v === ys[i]);
  };
  const beat = beatArg(at.beat);
  return (
    a.beat.w === beat.w &&
    a.beat.n === beat.n &&
    a.beat.d === beat.d &&
    a.line === at.line &&
    sameList(a.notes, at.notes) &&
    sameList(a.keys, at.keys)
  );
}

/**
 * Emits a problem unless the document ignores its id. Fixes that only do what "Go" does (`duplicatesGo`) are left
 * out: one clear action per row.
 */
export function emitProblem(ctx: CheckContext, draft: ProblemDraft): void {
  if (isIgnored(ctx.doc, draft.id)) return;
  const fixes = draft.fixes.filter((f) => !duplicatesGo(f, draft.at));
  ctx.emit(fixes.length === draft.fixes.length ? draft : { ...draft, fixes });
}

/** Lines a rule looks at: the given line (line-scoped call) or every line in export order. */
export function linesToCheck(ctx: Pick<CheckContext, 'doc'>, line?: LineId): readonly LineId[] {
  if (line !== undefined) return ctx.doc.lines.has(line) ? [line] : [];
  return ctx.doc.lineOrder;
}

// ---------------------------------------------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------------------------------------------

export type MessageParams = Readonly<Record<string, string | number>>;

/** A MessageRef (keys of namespace 'rules' live in src/features/checker/rules/rules.messages.ts). */
export function msg(key: string, params?: MessageParams): MessageRef {
  return params === undefined ? { key } : { key, params };
}

/**
 * Inspector field paths for the in-field badge (`ProblemLocation.field`): `<entity>.<model field>`. The inspector
 * (W2-06) looks problems up by these strings through the problems service (W2-12a).
 */
export const FIELD = Object.freeze({
  lineTexture: 'line.texture',
  lineAttachUI: 'line.attachUI',
  lineGifAutoLoop: 'line.gifAutoLoop',
  lineOpacity: 'line.alpha',
  lineColor: 'line.color',
  lineIncline: 'line.incline',
  lineText: 'line.text',
  lineSpeed: 'line.speed',
  lineLayers: 'line.layers',
  lineBpmfactor: 'line.rpe.bpmfactor',
  lineAnchor: 'line.rpe.anchor',
  lineSkewControl: 'line.rpe.skewControl',
  lineControlPos: 'line.controls.pos',
  lineControlAlpha: 'line.controls.alpha',
  lineControlSize: 'line.controls.size',
  lineControlY: 'line.controls.y',
  noteSpeed: 'note.speed',
  noteX: 'note.x',
  noteTime: 'note.t',
  noteEnd: 'note.end',
  noteKind: 'note.kind',
  noteHitsound: 'note.hitsound',
  segmentEase: 'segment.ease',
  keyValue: 'key.value',
  metaMusic: 'meta.music',
  metaPreview: 'meta.previewStart',
  pkgRpeVersion: 'pkg.rpeVersion',
  pkgSpeedMode: 'pkg.speedMode',
  assetPath: 'asset.path',
  effectShader: 'effect.shader',
  effectVars: 'effect.vars',
});

/** Field path of a note control curve. */
export function controlField(which: 'pos' | 'alpha' | 'size' | 'y'): string {
  return `line.controls.${which}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Fix references
// ---------------------------------------------------------------------------------------------------------------

/** Exact beat as JSON (`BeatArg` of app/registry/commands.ts). */
export type BeatArg = { readonly w: number; readonly n: number; readonly d: number };

export function beatArg(b: Beat): BeatArg {
  return { w: b.w, n: b.n, d: b.d };
}

/** JSON form of a model Ease (`problems.fix.setEase`). */
export type EaseArg =
  | { readonly kind: 'hold' }
  | { readonly kind: 'tween'; readonly id: number; readonly left: number; readonly right: number }
  | { readonly kind: 'bezier'; readonly p: readonly number[] };

export function easeArg(e: Ease): EaseArg {
  switch (e.kind) {
    case 'hold':
      return { kind: 'hold' };
    case 'tween':
      return { kind: 'tween', id: e.id, left: e.left, right: e.right };
    case 'bezier':
      return { kind: 'bezier', p: [e.p[0], e.p[1], e.p[2], e.p[3]] };
  }
}

/** JSON form of a ControlCurve (`problems.fix.setControl`). */
export type ControlCurveArg = {
  readonly points: ReadonlyArray<{
    readonly x: number;
    readonly easing: number;
    readonly value: number;
    readonly extra?: ReadonlyArray<readonly [string, number]>;
  }>;
};

export function controlCurveArg(c: ControlCurve): ControlCurveArg {
  return {
    points: c.points.map((p) =>
      p.extra === undefined
        ? { x: p.x, easing: p.easing, value: p.value }
        : { x: p.x, easing: p.easing, value: p.value, extra: p.extra.map(([k, v]) => [k, v] as const) },
    ),
  };
}

/**
 * Document-changing fixes referenced by the rules: W2-12a's own `problems.fix.*` commands (registered in
 * src/features/checker/fixes.ts, each calling Core command bodies in ONE undoable transaction). The argument shape
 * of every id is `FixArgs[id]`; numeric entity ids are the document's ids (LineId, NoteId, KeyId, TrackId, ...).
 * The list and the semantics are specified in docs/design/change-requests/W2-12b.md.
 */
export const FIX_COMMANDS = [
  'problems.fix.setLineOpacityFull',
  'problems.fix.holdLeadIn',
  'problems.fix.setEase',
  'problems.fix.setKeyValue',
  'problems.fix.clampKeyValues',
  'problems.fix.removeGifProgress',
  'problems.fix.clampPreviewRange',
  'problems.fix.snapNotesX',
  'problems.fix.deleteNotes',
  'problems.fix.moveNotesLater',
  'problems.fix.alignNoteTimes',
  'problems.fix.setIncline',
  'problems.fix.resetRpeLineFields',
  'problems.fix.addWhiteColorKey',
  'problems.fix.holdVarLeadIn',
  'problems.fix.renameAsset',
  'problems.fix.renameHitsoundAsset',
  'problems.fix.deleteEffect',
  'problems.fix.setEffectVar',
  'problems.fix.writeIntegerRpeVersion',
  'problems.fix.extendHolds',
  'problems.fix.convertNotes',
  'problems.fix.setControl',
  'problems.fix.removeAssetReferences',
  'problems.fix.convertAudioToWav',
  'problems.fix.convertImageToPng',
  'problems.fix.reencodeGif',
  'problems.fix.downscaleImage',
  'problems.fix.moveNotesToNewLine',
  'problems.fix.removeIgnoredLineData',
] as const;

export type FixCommandId = (typeof FIX_COMMANDS)[number];

/** Argument shape of each `problems.fix.*` id (JSON; see FIX_COMMANDS). */
export interface FixArgs {
  /** Opacity track of the line (layer 0): one key 255 at beat 0 (Core.keys.ensureTrack). PH-10, PH-11. */
  'problems.fix.setLineOpacityFull': { readonly line: number };
  /** Core.keys.holdLeadIn. PH-09. */
  'problems.fix.holdLeadIn': { readonly track: number };
  /** Core.keys.setEase on the segments starting at these keys. PH-03, PH-06, PH-08, PH-16, PH-20, PH-39. */
  'problems.fix.setEase': { readonly segments: readonly number[]; readonly ease: EaseArg };
  /** Core.keys.setKeyValues on one key ('out' = leave value, 'in' = arrive value). PH-15. */
  'problems.fix.setKeyValue': {
    readonly key: number;
    readonly part: 'in' | 'out' | 'both';
    readonly value: number | string | readonly number[];
  };
  /** Clamp vIn and vOut of each key into [min, max] (Core.keys.setKeyValues per key and side). GP-10. */
  'problems.fix.clampKeyValues': { readonly keys: readonly number[]; readonly min: number; readonly max: number };
  /**
   * Delete the gif tracks of the lines and turn "Play automatically" off (Line.gifAutoLoop false), so Phira
   * loads the texture as a still image. PH-04, PH-35.
   */
  'problems.fix.removeGifProgress': { readonly lines: readonly number[] };
  /** Core.doc.patchMeta { previewStart: start, previewEnd: end } (values computed by the rule). PH-36. */
  'problems.fix.clampPreviewRange': { readonly start: number; readonly end: number | null };
  /** Core.notes.snapNotesX with the editor's X grid (notes beyond the grid snap to its outermost line). GP-08. */
  'problems.fix.snapNotesX': { readonly notes: readonly number[] };
  /** Core.notes.deleteNotes. GP-03. */
  'problems.fix.deleteNotes': { readonly notes: readonly number[] };
  /** Core.notes.moveNotes by one step of the editor's current snap (later in time). GP-01. */
  'problems.fix.moveNotesLater': { readonly notes: readonly number[] };
  /** Core.notes.setNoteTimes: every note to `beat` (holds keep their length; raw imported triples dropped). GP-09. */
  'problems.fix.alignNoteTimes': { readonly notes: readonly number[]; readonly beat: BeatArg };
  /** Core.lines.setIncline (constant tilt, replaces the animated track). PH-18. */
  'problems.fix.setIncline': { readonly line: number; readonly degrees: number };
  /**
   * Reset Phira-ignored RPE line fields (Line.rpe): bpmfactor 1, anchor [0.5, 0.5], skewControl null. Phira's
   * rendering does not change. PH-22.
   */
  'problems.fix.resetRpeLineFields': {
    readonly line: number;
    readonly fields: readonly ('bpmfactor' | 'anchor' | 'skewControl')[];
  };
  /** Colour track of the line: one key [255, 255, 255] at beat 0 (Core.keys.ensureTrack). PH-29. */
  'problems.fix.addWhiteColorKey': { readonly line: number };
  /**
   * Insert a key at `beat` on an effect / video variable track whose first key is later, with the first key's value
   * and a Stay (hold) easing, so the variable holds its first value from the effect start instead of being
   * extrapolated (Core.keys.insertKey + Core.keys.setEase). PH-30.
   */
  'problems.fix.holdVarLeadIn': { readonly track: number; readonly beat: BeatArg };
  /** Core.doc.renameAsset (path suggested by the rule; references follow the asset id). PH-05, PH-31. */
  'problems.fix.renameAsset': { readonly asset: string; readonly path: string };
  /**
   * Core.doc.renameAsset to `path`, then Core.notes.setHitsound { kind: 'asset', asset } on `notes` (the notes that
   * named the reserved file and therefore played Phira's built-in sound). PH-38.
   */
  'problems.fix.renameHitsoundAsset': {
    readonly asset: string;
    readonly path: string;
    readonly notes: readonly number[];
  };
  /** Core.doc.deleteEffect. PH-07, PH-33. */
  'problems.fix.deleteEffect': { readonly effect: number };
  /** Core.doc.setEffectVar with a constant of the shader's type (value computed by the rule). PH-07. */
  'problems.fix.setEffectVar': {
    readonly effect: number;
    readonly name: string;
    readonly type: EffectVarType;
    readonly value: number | readonly number[];
  };
  /** Core.doc.patchPkg { rpeVersion: version, rpeVersionRaw: null }. PH-34. */
  'problems.fix.writeIntegerRpeVersion': { readonly version: number };
  /** Hold end = start + one step of the editor's current snap (Core.notes.setNoteTimes). PH-37. */
  'problems.fix.extendHolds': { readonly notes: readonly number[] };
  /** Core.notes.convertNotes. PH-37. */
  'problems.fix.convertNotes': { readonly notes: readonly number[]; readonly kind: NoteKind };
  /** Core.lines.setControl with the curve computed by the rule. PH-17. */
  'problems.fix.setControl': {
    readonly line: number;
    readonly which: 'pos' | 'alpha' | 'size' | 'y';
    readonly curve: ControlCurveArg;
  };
  /** Core.doc.removeAsset { references: 'remove' }. PH-01. */
  'problems.fix.removeAssetReferences': { readonly asset: string };
  /** Decode in the browser, encode WAV (core/audio-dsp/wav.ts), add the asset and Core.doc.replaceAsset. PH-01, PH-02. */
  'problems.fix.convertAudioToWav': { readonly asset: string };
  /** Decode in the browser, encode PNG (io worker), add the asset and Core.doc.replaceAsset. PH-01. */
  'problems.fix.convertImageToPng': { readonly asset: string };
  /** Re-encode the GIF with every frame delay set to `frameMs` (io worker), then Core.doc.replaceAsset. PH-04. */
  'problems.fix.reencodeGif': { readonly asset: string; readonly frameMs: number };
  /** Downscale so the longer side is at most `maxSide` px (io worker), then Core.doc.replaceAsset. PH-27. */
  'problems.fix.downscaleImage': { readonly asset: string; readonly maxSide: number };
  /**
   * Duplicate the line's motion into a new normal line (Core.lines.duplicateLine without notes, then
   * Core.lines.setAttachUI null on the copy and a constant opacity 0 so only its notes show), then
   * Core.notes.moveNotesToLine `notes` to the copy. PH-12.
   */
  'problems.fix.moveNotesToNewLine': { readonly line: number; readonly notes: readonly number[] };
  /**
   * Remove data Phira ignores on this line kind: the line's tracks of `props` (Core.keys.resetProperty) and the
   * `LineRpeData.ignored` entries (writeBack 'extended') whose key is in `keys`. PH-24.
   */
  'problems.fix.removeIgnoredLineData': {
    readonly line: number;
    readonly props: readonly LineProp[];
    readonly keys: readonly string[];
  };
}

/**
 * Navigation fixes: commands contracted in app/registry/commands.ts `CommandArgs` (mirrored here because rule
 * files cannot import the app layer; a test asserts the shapes agree).
 */
export interface NavArgs {
  'nav.reveal': {
    readonly beat: BeatArg;
    readonly line?: number;
    readonly notes?: readonly number[];
    readonly keys?: readonly number[];
  };
  'assets.replace': { readonly asset: string };
  'assets.add': { readonly role?: 'music' | 'illustration' | 'texture' | 'gif' | 'hitsound' | 'shader' | 'video' };
  'inspector.focusField': { readonly field: string };
  'project.settings': { readonly section?: 'display' | 'gameplay' | 'speed' | 'export' };
  'tools.flatten': { readonly line: number; readonly prop: string };
}

export type NavCommandId = keyof NavArgs;

/** Converts a typed argument record to the FixRef's Json (every args type above is plain JSON). */
function toJson(v: unknown): Json {
  return v as Json;
}

/** A document-changing fix (`problems.fix.*`). */
export function fixRef<K extends FixCommandId>(command: K, label: MessageRef, args: FixArgs[K]): FixRef {
  return { label, command, args: toJson(args) };
}

/** A navigation fix through a contracted command id. */
export function navFix<K extends NavCommandId>(command: K, label: MessageRef, args: NavArgs[K]): FixRef {
  return { label, command, args: toJson(args) };
}

/** "Go to" through `nav.reveal` (seek, select, focus the line). */
export function revealFix(
  label: MessageRef,
  beat: Beat,
  sel: { readonly line?: LineId; readonly notes?: readonly NoteId[]; readonly keys?: readonly KeyId[] } = {},
): FixRef {
  const args: {
    beat: BeatArg;
    line?: number;
    notes?: readonly number[];
    keys?: readonly number[];
  } = { beat: beatArg(beat) };
  if (sel.line !== undefined) args.line = sel.line;
  if (sel.notes !== undefined && sel.notes.length > 0) args.notes = [...sel.notes];
  if (sel.keys !== undefined && sel.keys.length > 0) args.keys = [...sel.keys];
  return navFix('nav.reveal', label, args);
}

// ---------------------------------------------------------------------------------------------------------------
// Formatting of message parameters
// ---------------------------------------------------------------------------------------------------------------

/** "12:3/4" (RPE style). */
export function fmtBeat(b: Beat): string {
  return formatBeat(b, 'colon');
}

/** "0:24.512". */
export function fmtSec(s: Sec): string {
  return formatSec(s);
}

/** Whole milliseconds of a duration in seconds. */
export function msOf(sec: number): number {
  return Math.round(sec * 1000);
}

/** A number for messages: at most `digits` decimals, no trailing zeros, no "-0". */
export function fmtNum(v: number, digits = 3): string {
  if (!Number.isFinite(v)) return String(v);
  const f = 10 ** digits;
  const r = Math.round(v * f) / f;
  return Object.is(r, -0) ? '0' : String(r);
}

/** How a line is named in messages: its name, or "#<export index>" when unnamed. */
export function lineLabel(doc: Pick<ChartDoc, 'lines'>, idx: Pick<DocIndexes, 'lineIndex'>, line: LineId): string {
  const rec = doc.lines.get(line);
  const name = rec?.name.trim() ?? '';
  return name === '' ? `#${String(idx.lineIndex(line))}` : name;
}

/**
 * Message parameters describing an easing, for the shared EASE fragment of rules.messages.ts:
 * `easeKind` ('hold' | 'tween' | 'bezier') selects the translated wording ("Stay, then jump", "the custom curve
 * (0.2, 1.4, 0.6, 1)"); only RPE identifiers are passed as raw text (`ease`: "OutBack", "Linear", or "#<id>" for an
 * id outside the catalogue). `easeSlice` 'part' adds the used part of the curve (`easeLeft`, `easeRight`). Every
 * parameter is always present, so the message formats whatever the kind.
 */
export function easeParams(e: Ease): MessageParams {
  switch (e.kind) {
    case 'hold':
      return { easeKind: 'hold', ease: '', easePoints: '', easeSlice: 'whole', easeLeft: '0', easeRight: '1' };
    case 'tween': {
      const entry = catalogEntry(e.id);
      const whole = e.left === 0 && e.right === 1;
      return {
        easeKind: 'tween',
        ease: entry?.rpeName ?? `#${String(e.id)}`,
        easePoints: '',
        easeSlice: whole ? 'whole' : 'part',
        easeLeft: fmtNum(e.left, 6),
        easeRight: fmtNum(e.right, 6),
      };
    }
    case 'bezier':
      return {
        easeKind: 'bezier',
        ease: '',
        easePoints: fmtPoints(e.p),
        easeSlice: 'whole',
        easeLeft: '0',
        easeRight: '1',
      };
  }
}

/** The same easing without its slice (a tween over the whole curve); other kinds unchanged. */
export function wholeEase(e: Ease): Ease {
  return e.kind === 'tween' ? { kind: 'tween', id: e.id, left: 0, right: 1 } : e;
}

/** "(0.2, 1.4, 0.6, 1)". */
export function fmtPoints(p: BezierPoints | readonly number[]): string {
  return `(${p.map((v) => fmtNum(v)).join(', ')})`;
}

/** A short excerpt of a text value for messages (quotes added by the message). */
export function textExcerpt(s: string, max = 24): string {
  if (charLen(s) <= max) return s;
  let out = '';
  let n = 0;
  for (const ch of s) {
    if (n === max - 1) break;
    out += ch;
    n++;
  }
  return `${out}…`;
}

// ---------------------------------------------------------------------------------------------------------------
// Document access
// ---------------------------------------------------------------------------------------------------------------

/** The line owning a track (null for effect / video variable tracks). */
export function lineOfTrack(doc: Pick<ChartDoc, 'tracks'>, track: TrackId): LineId | null {
  const t = doc.tracks.get(track);
  return t !== undefined && t.owner.kind === 'line' ? t.owner.line : null;
}

/** Keys of a track sorted by beat. */
export function trackKeys(ctx: Pick<CheckContext, 'doc' | 'idx'>, track: TrackId): Key[] {
  const out: Key[] = [];
  for (const id of ctx.idx.keysByTrack(track)) {
    const k = ctx.doc.keys.get(id);
    if (k !== undefined) out.push(k);
  }
  return out;
}

/** Tracks of one property of a line, layered properties ordered by layer. */
export function propTracks(ctx: Pick<CheckContext, 'doc' | 'idx'>, line: LineId, prop: LineProp): Track[] {
  const out: Track[] = [];
  for (const id of ctx.idx.tracksOfLine(line).get(prop) ?? []) {
    const t = ctx.doc.tracks.get(id);
    if (t !== undefined) out.push(t);
  }
  return out;
}

/** Whether the line has at least one track of the property. */
export function hasProp(ctx: Pick<CheckContext, 'idx'>, line: LineId, prop: LineProp): boolean {
  return (ctx.idx.tracksOfLine(line).get(prop)?.length ?? 0) > 0;
}

/** Layer index of a line track (0 for extended properties and non-line tracks). */
export function layerOf(track: Track): number {
  return track.owner.kind === 'line' ? track.owner.layer : 0;
}

/** Notes of a line (sorted by beat, x, id). */
export function lineNotes(ctx: Pick<CheckContext, 'doc' | 'idx'>, line: LineId): Note[] {
  const out: Note[] = [];
  for (const id of ctx.idx.notesByLine(line)) {
    const n = ctx.doc.notes.get(id);
    if (n !== undefined) out.push(n);
  }
  return out;
}

/** Phira's derived kind of a line (F§9.5). */
export function kindOf(ctx: Pick<CheckContext, 'doc' | 'idx'>, line: LineId): LineKind {
  return lineKind(ctx.doc, ctx.idx, line);
}

/** The value a segment starting at key `i` arrives at (holds arrive as jumps: the next key's vIn either way). */
export function segmentEnd(keys: readonly Key[], i: number): TrackValue | undefined {
  return keys[i + 1]?.vIn;
}

/** Total offset Phira applies between music time and chart time (META.offset + info.yml offset), seconds. */
export function offsetSec(doc: Pick<ChartDoc, 'timing' | 'pkg'>): number {
  return doc.timing.offsetMs / 1000 + doc.pkg.infoOffsetSec;
}

/** Two music lengths further apart than this are different files (a stale decode after a music replacement). */
const SAME_FILE_LENGTH_SLACK_SEC = 1;

function positiveLength(v: number | null | undefined): number | null {
  return v !== null && v !== undefined && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * Length of the music in seconds for the song-length rules (PH-36, GP-04), null when unknown. Two sources:
 *
 *  - the music asset's `meta.durationSec`: tied to the asset id (assets are immutable), so it follows a replaced
 *    music file at once. Adding or replacing music in the editor stores a full decode; an imported package stores
 *    the header probe (formats/package/sniff.ts), which can be an estimate (e.g. a VBR MP3 without a frame count);
 *  - the decoded length of the context (`songLengthSec`), which describes the previous file until the new one is
 *    decoded.
 *
 * When the runtime says which asset the decoded length belongs to (`CheckContext.songLengthAsset`, CR-W2-12b-5),
 * a decode of another file is ignored and a decode of the current file wins over a differing
 * probe. Without that information the decode is trusted only when both agree within a second (the same file). Two
 * lengths of the same file that agree use the smaller one (decoders may add padding; the preview margin must hold
 * for the shorter).
 */
export function musicLengthSec(ctx: Pick<CheckContext, 'doc' | 'songLengthSec' | 'songLengthAsset'>): number | null {
  const music = ctx.doc.meta.music;
  const asset = music === null ? undefined : ctx.doc.assets.get(music);
  const fromAsset = positiveLength(asset?.meta?.durationSec);
  const decoded = positiveLength(ctx.songLengthSec);
  const decodedFrom = ctx.songLengthAsset;
  if (decoded === null) return fromAsset;
  if (decodedFrom !== undefined) {
    // Provenance known: a decode of the previous music file says nothing about the current one.
    if (decodedFrom !== music) return fromAsset;
    if (fromAsset === null) return decoded;
    return Math.abs(fromAsset - decoded) <= SAME_FILE_LENGTH_SLACK_SEC ? Math.min(fromAsset, decoded) : decoded;
  }
  if (fromAsset === null) return decoded;
  return Math.abs(fromAsset - decoded) <= SAME_FILE_LENGTH_SLACK_SEC ? Math.min(fromAsset, decoded) : fromAsset;
}

/** A preview range that Phira accepts for a song of `len` seconds, as close to the current one as possible. */
export function clampedPreviewRange(
  start: number,
  end: number | null,
  len: number | null,
): { readonly start: number; readonly end: number | null } | null {
  const s = Number.isFinite(start) && start > 0 ? start : 0;
  if (len === null) {
    if (end === null || end > s) return { start: s, end };
    return { start: Math.max(0, end - 15), end: end > 0 ? end : null };
  }
  const limit = len - PREVIEW_END_MARGIN_SEC;
  if (!(limit > 0)) return null;
  if (end === null) {
    if (s + PREVIEW_NULL_END_SEC <= limit) return { start: s, end: null };
    if (limit >= PREVIEW_NULL_END_SEC) return { start: limit - PREVIEW_NULL_END_SEC, end: null };
    return { start: 0, end: limit };
  }
  const e = Math.min(end, limit);
  if (e > s) return { start: s, end: e };
  const start2 = Math.max(0, e - 15);
  return e > start2 ? { start: start2, end: e } : { start: 0, end: limit };
}

// ---------------------------------------------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------------------------------------------

/** Effective aspect ratio Phira uses on the reference viewport (player override, forceAspectRatio; R§1.2). */
export function effectiveAspect(
  ctx: Pick<CheckContext, 'doc' | 'player' | 'refViewport'>,
  viewport: { readonly w: number; readonly h: number } = ctx.refViewport,
): number {
  return computeViewport(viewport.w, viewport.h, ctx.doc.pkg, ctx.player.aspectOverride).ar;
}

/** Visible chart area in world units for an aspect ratio. */
export interface ScreenBounds {
  readonly xMin: number;
  readonly xMax: number;
  readonly yMin: number;
  readonly yMax: number;
}

export function screenBounds(ar: number): ScreenBounds {
  return { xMin: -1, xMax: 1, yMin: -1 / ar, yMax: 1 / ar };
}

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

/** Whether a world point is inside the visible chart area (with an optional margin in world units). */
export function isOnScreen(p: WorldPoint, ar: number, margin = 0): boolean {
  const b = screenBounds(ar);
  return p.x >= b.xMin - margin && p.x <= b.xMax + margin && p.y >= b.yMin - margin && p.y <= b.yMax + margin;
}

/** World point to RPE px (X = x * 675, Y = y * ar * 450). */
export function worldToRpe(p: WorldPoint, ar: number): WorldPoint {
  return { x: p.x * RPE_X_PER_WORLD, y: p.y * ar * RPE_Y_PER_WORLD_AR };
}

/** A copied line frame (LineStateRt objects are pooled by the engine). */
export interface LineFrame {
  readonly line: LineId;
  /** Translate(x, y) · Rotate(rot), local -> world, [a, b, c, d, e, f] (x' = a x + c y + e, y' = b x + d y + f). */
  readonly m: readonly [number, number, number, number, number, number];
  readonly alpha: number;
  readonly uiMover: boolean;
  readonly height: number;
}

/**
 * Line frames already evaluated for one engine revision and aspect ratio (UX-092): the pair rules ask for the same
 * frames over and over (every note of a chord at the chord's time, every partner of a hold's start), and a line state
 * walks the whole parent chain. Dropped when the engine changes (its revision bumps on every note, line or tempo
 * change) and when it grows past FRAME_MEMO_MAX entries.
 */
interface FrameMemo {
  readonly engine: Engine;
  readonly revision: number;
  readonly ar: number;
  size: number;
  readonly byLine: Map<LineId, Map<number, LineFrame>>;
}

const FRAME_MEMO_MAX = 200_000;
let frameMemo: FrameMemo | null = null;

function memoFor(engine: Engine, ar: number): FrameMemo {
  const m = frameMemo;
  if (m !== null && m.engine === engine && m.revision === engine.revision && m.ar === ar && m.size < FRAME_MEMO_MAX) {
    return m;
  }
  const next: FrameMemo = { engine, revision: engine.revision, ar, size: 0, byLine: new Map() };
  frameMemo = next;
  return next;
}

/** State of a line at chart time t evaluated with aspect ratio `ar` (Engine.lineState's `ar`, CR-W1-05b-1). */
export function lineFrameAt(engine: Engine, line: LineId, t: Sec, ar: number): LineFrame {
  const memo = memoFor(engine, ar);
  const known = memo.byLine.get(line)?.get(t);
  if (known !== undefined) return known;
  const s = engine.lineState(line, t, undefined, ar);
  const frame: LineFrame = {
    line,
    m: [s.m[0], s.m[1], s.m[2], s.m[3], s.m[4], s.m[5]],
    alpha: s.alpha,
    uiMover: s.uiMover,
    height: s.height,
  };
  // lineState may have compiled pending changes: remember the frame under the revision it was computed with.
  const target = memoFor(engine, ar);
  let byT = target.byLine.get(line);
  if (byT === undefined) {
    byT = new Map();
    target.byLine.set(line, byT);
  }
  byT.set(t, frame);
  target.size++;
  return frame;
}

/** Local -> world. */
export function frameToWorld(f: LineFrame, lx: number, ly: number): WorldPoint {
  const [a, b, c, d, e, g] = f.m;
  return { x: a * lx + c * ly + e, y: b * lx + d * ly + g };
}

/** World -> local. */
export function worldToFrame(f: LineFrame, p: WorldPoint): WorldPoint {
  const [a, b, c, d, e, g] = f.m;
  const det = a * d - b * c;
  const dx = p.x - e;
  const dy = p.y - g;
  if (det === 0 || !Number.isFinite(det)) return { x: Number.NaN, y: Number.NaN };
  return { x: (d * dx - c * dy) / det, y: (a * dy - b * dx) / det };
}

/** What the judge needs of a note: its line and raw positionX (RPE x px). */
export interface JudgeSpot {
  readonly line: LineId;
  readonly x: number;
}

/**
 * The note's judge point in world space at chart time t: the raw positionX on its line (F§11.6: posControl and
 * incline move the drawn note, not its hit area; along the line's normal any position is accepted).
 */
export function judgePoint(engine: Engine, spot: JudgeSpot, t: Sec, ar: number): WorldPoint {
  return frameToWorld(lineFrameAt(engine, spot.line, t, ar), spot.x / RPE_X_PER_WORLD, 0);
}

/**
 * Judge distance in RPE x px between a touch on `from`'s judge point and the note `to`, both at chart time t:
 * Phira transforms the touch into `to`'s line frame and compares only the x distance along that line (F§11.6).
 */
export function judgeDistancePx(engine: Engine, from: JudgeSpot, to: JudgeSpot, t: Sec, ar: number): number {
  if (from.line === to.line) {
    // Same frame: the distance along the line is exact without the matrix round trip.
    return Math.abs(from.x - to.x);
  }
  const touch = judgePoint(engine, from, t, ar);
  const local = worldToFrame(lineFrameAt(engine, to.line, t, ar), touch);
  return Math.abs(local.x * RPE_X_PER_WORLD - to.x);
}

/** Smaller of the two judge distances (a touch on either note reaching the other). */
export function mutualJudgeDistancePx(engine: Engine, a: JudgeSpot, b: JudgeSpot, t: Sec, ar: number): number {
  if (a.line === b.line) return Math.abs(a.x - b.x);
  return Math.min(judgeDistancePx(engine, a, b, t, ar), judgeDistancePx(engine, b, a, t, ar));
}

/**
 * Distance on screen between the judge points of two notes at chart time t, in RPE x px (world distance x 675):
 * where the notes are drawn as they are hit. Used where players SEE notes on the same spot (overlaps, a note on a
 * hold's end); the judge band (`judgeDistancePx`) is what matters when one touch slides through another note.
 */
export function screenDistancePx(engine: Engine, a: JudgeSpot, b: JudgeSpot, t: Sec, ar: number): number {
  if (a.line === b.line) return Math.abs(a.x - b.x);
  const pa = judgePoint(engine, a, t, ar);
  const pb = judgePoint(engine, b, t, ar);
  return Math.hypot(pa.x - pb.x, pa.y - pb.y) * RPE_X_PER_WORLD;
}

/**
 * Whether the note's judge band crosses the visible screen: Phira accepts a touch anywhere on the line through the
 * judge point along the line's local y axis (only the x distance along the line counts), so a note whose judge
 * point is off screen can still be hit when that band passes through the screen.
 */
export function judgeBandCrossesScreen(f: LineFrame, xLocal: number, ar: number): boolean {
  const p = frameToWorld(f, xLocal, 0);
  const dir = { x: f.m[2], y: f.m[3] };
  const b = screenBounds(ar);
  let lo = -Infinity;
  let hi = Infinity;
  const axis = (p0: number, d: number, min: number, max: number): boolean => {
    if (Math.abs(d) < 1e-12) return p0 >= min && p0 <= max;
    let s0 = (min - p0) / d;
    let s1 = (max - p0) / d;
    if (s0 > s1) [s0, s1] = [s1, s0];
    lo = Math.max(lo, s0);
    hi = Math.min(hi, s1);
    return lo <= hi;
  };
  if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return false;
  return axis(p.x, dir.x, b.xMin, b.xMax) && axis(p.y, dir.y, b.yMin, b.yMax);
}

/**
 * Read time: seconds before its hit during which the note is continuously visible in Phira's autoplay, computed by
 * the engine with the context's player settings, skin and reference viewport. null = never visible before its hit.
 * `atLeast`: the caller only needs to know whether the read time reaches this many seconds; the engine's bounded
 * query (Engine.noteAppearSec, CR-W2-12b-3) may stop probing there and return a lower bound above `atLeast`, so the
 * answer to "below `atLeast`?" is exact either way.
 */
export function readTimeSec(ctx: CheckContext, note: NoteId, atLeast?: number): number | null {
  const { w, h } = ctx.refViewport;
  return ctx.engine.noteAppearSec(note, ctx.player, ctx.skin, w, h, atLeast);
}

// ---------------------------------------------------------------------------------------------------------------
// Notes by time
// ---------------------------------------------------------------------------------------------------------------

export interface TimedNote {
  readonly id: NoteId;
  readonly note: Note;
  /** Hit time (chart seconds, Phira-exact). */
  readonly sec: Sec;
  /** End time (holds) or the hit time. */
  readonly endSec: Sec;
}

interface TimedCache {
  readonly doc: ChartDoc;
  readonly revision: number;
  readonly count: number;
  readonly list: readonly TimedNote[];
}

const timedCache = new WeakMap<Engine, TimedCache>();

/**
 * Time ranges (chart seconds, sorted, disjoint) an incremental pass asks a windowed rule to look at (runner.ts):
 * `notesByTime` then lists only the notes whose time span reaches into one of them.
 */
export interface NoteWindow {
  readonly ranges: readonly (readonly [number, number])[];
}

/** The window of the work unit running now, when the runner set one on the context (`noteWindow`). */
function windowOf(ctx: object): NoteWindow | undefined {
  return (ctx as { readonly noteWindow?: NoteWindow }).noteWindow;
}

/**
 * A chart rule that can check a time window instead of the whole chart (UX-092: a tap re-checks the pairs around
 * it, not every pair of the chart). It reads notes only through `notesByTime` / `realNotesByTime`, every problem it
 * reports carries its anchor time `at.sec`, and that problem only depends on notes within `windowPadSec` of the
 * anchor (start or end, for holds). The runner then re-runs it with a window around the notes a change touched and
 * keeps the problems anchored elsewhere.
 */
export interface WindowedCheckRule extends CheckRule {
  windowPadSec(ctx: CheckContext): number;
}

export function isWindowedRule(rule: CheckRule): rule is WindowedCheckRule {
  return typeof (rule as Partial<WindowedCheckRule>).windowPadSec === 'function';
}

/** Whether a time span [from, to] reaches into one of the window's ranges. */
export function inWindow(w: NoteWindow, from: number, to: number = from): boolean {
  for (const [a, b] of w.ranges) if (to >= a && from <= b) return true;
  return false;
}

/**
 * Every note with its Phira seconds, sorted by (sec, id). Cached per engine revision (the engine bumps it on every
 * note, line or tempo change), so the gameplay rules of one pass share one table. Inside a windowed work unit
 * (`WindowedCheckRule`) only the notes whose span [sec, endSec] reaches into the window are listed.
 */
export function notesByTime(ctx: Pick<CheckContext, 'doc' | 'engine'>): readonly TimedNote[] {
  const all = allNotesByTime(ctx);
  const w = windowOf(ctx);
  return w === undefined ? all : all.filter((n) => inWindow(w, n.sec, n.endSec));
}

function allNotesByTime(ctx: Pick<CheckContext, 'doc' | 'engine'>): readonly TimedNote[] {
  const { doc, engine } = ctx;
  engine.sync();
  const cached = timedCache.get(engine);
  if (
    cached !== undefined &&
    cached.doc === doc &&
    cached.revision === engine.revision &&
    cached.count === doc.notes.size
  ) {
    return cached.list;
  }
  const list: TimedNote[] = [];
  for (const note of doc.notes.values()) {
    if (!doc.lines.has(note.line)) continue;
    const sec = engine.noteSec(note.id);
    const endSec = note.kind === 'hold' ? engine.noteEndSec(note.id) : sec;
    list.push({ id: note.id, note, sec, endSec });
  }
  list.sort((a, b) => a.sec - b.sec || a.id - b.id);
  timedCache.set(engine, { doc, revision: engine.revision, count: doc.notes.size, list });
  return list;
}

/** Real (judged) notes only: fake notes are not judged, scored or counted (F§11.6). */
export function realNotesByTime(ctx: Pick<CheckContext, 'doc' | 'engine'>): readonly TimedNote[] {
  return notesByTime(ctx).filter((n) => !n.note.fake);
}

/**
 * Calls `cb(a, b, dt)` for every ordered pair a before b (b.sec - a.sec in [0, maxDt]) of a time-sorted list,
 * a != b. Pairs at equal times are visited once, in list order.
 */
export function forEachPairWithin(
  list: readonly TimedNote[],
  maxDt: number,
  cb: (a: TimedNote, b: TimedNote, dt: number) => void,
): void {
  for (let i = 0; i < list.length; i++) {
    const a = list[i];
    if (a === undefined) continue;
    for (let j = i + 1; j < list.length; j++) {
      const b = list[j];
      if (b === undefined) break;
      const dt = b.sec - a.sec;
      if (dt > maxDt + WINDOW_EPS_SEC) break;
      cb(a, b, dt);
    }
  }
}

/** Index of the first note with sec >= t in a time-sorted list (binary search). */
export function firstAtOrAfter(list: readonly TimedNote[], t: Sec): number {
  let lo = 0;
  let hi = list.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    const m = list[mid];
    if (m !== undefined && m.sec < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

// ---------------------------------------------------------------------------------------------------------------
// Media and package paths
// ---------------------------------------------------------------------------------------------------------------

export type MediaUse = 'image' | 'audio' | 'video' | 'shader';

/** Sniffed codecs (formats/package/sniff.ts) Phira decodes, per use (P§2.7). */
const PHIRA_CODECS: Readonly<Record<MediaUse, ReadonlySet<string>>> = {
  image: new Set(['png', 'jpg', 'webp', 'bmp', 'gif']),
  audio: new Set(['mp3', 'ogg-vorbis', 'wav', 'flac', 'aac']),
  video: new Set(['mp4', 'mov', 'webm', 'mkv', 'avi', 'ogv']),
  shader: new Set(['glsl', 'text']),
};

/** Codec families that can never serve a use (an image used as a hitsound, audio used as a texture, ...). */
const KNOWN_KIND: Readonly<Record<string, MediaUse>> = {
  png: 'image',
  jpg: 'image',
  'jpg-unsupported': 'image',
  webp: 'image',
  bmp: 'image',
  gif: 'image',
  avif: 'image',
  heic: 'image',
  ppm: 'image',
  tiff: 'image',
  ico: 'image',
  svg: 'image',
  mp3: 'audio',
  mp2: 'audio',
  'ogg-vorbis': 'audio',
  opus: 'audio',
  'ogg-flac': 'audio',
  'ogg-speex': 'audio',
  ogg: 'audio',
  wav: 'audio',
  'wav-unsupported': 'audio',
  flac: 'audio',
  aac: 'audio',
  m4a: 'audio',
  mka: 'audio',
  weba: 'audio',
  mp4: 'video',
  mov: 'video',
  webm: 'video',
  mkv: 'video',
  avi: 'video',
  ogv: 'video',
  glsl: 'shader',
  text: 'shader',
};

export interface MediaStatus {
  readonly status: 'ok' | 'missing' | 'undecodable';
  /** Sniffed codec when known. */
  readonly codec: string | null;
}

/**
 * Whether Phira can load the asset for a use: missing (absent from the project or a placeholder for a file the
 * imported package lacked) or undecodable (sniffed codec Phira cannot decode for that use: avif, ppm, opus, m4a,
 * an audio file as a texture, ...). Assets whose codec was never sniffed count as decodable.
 */
export function mediaStatus(asset: AssetRef | undefined, use: MediaUse): MediaStatus {
  if (asset === undefined) return { status: 'missing', codec: null };
  const codec = asset.meta?.codec ?? null;
  if (asset.missing === true) return { status: 'missing', codec };
  if (use === 'shader') {
    // Shaders are UTF-8 text; only a recognised binary media file is certainly unreadable as GLSL.
    const kind = codec === null ? undefined : KNOWN_KIND[codec];
    return { status: kind !== undefined && kind !== 'shader' ? 'undecodable' : 'ok', codec };
  }
  if (codec !== null) {
    if (PHIRA_CODECS[use].has(codec)) {
      return { status: asset.meta?.phiraDecodable === false ? 'undecodable' : 'ok', codec };
    }
    if (KNOWN_KIND[codec] !== undefined || asset.meta?.phiraDecodable === false) {
      return { status: 'undecodable', codec };
    }
    return { status: 'ok', codec };
  }
  return { status: asset.meta?.phiraDecodable === false ? 'undecodable' : 'ok', codec };
}

/** Whether a browser can most likely decode the asset for a conversion fix (it is in the project, not a placeholder). */
export function browserCanConvert(asset: AssetRef | undefined, use: 'image' | 'audio'): boolean {
  if (asset === undefined || asset.missing === true) return false;
  const codec = asset.meta?.codec;
  if (codec === undefined) return true;
  return KNOWN_KIND[codec] === use;
}

/** True for a package path Phira's import treats as a resource pack (case-sensitive suffix, P§2.1). */
export function isSkinTriggerName(path: string): boolean {
  return path.endsWith('click.png');
}

/** True for the batch-export marker at the package root (P§2.1). */
export function isBatchExportMarker(path: string): boolean {
  return path === 'export.json';
}

/** True when the path contains a character outside printable ASCII. */
export function hasNonAscii(path: string): boolean {
  for (let i = 0; i < path.length; i++) {
    const c = path.charCodeAt(i);
    if (c < 0x20 || c > 0x7e) return true;
  }
  return false;
}

function splitExt(path: string): { readonly dir: string; readonly stem: string; readonly ext: string } {
  const slash = path.lastIndexOf('/');
  const dir = slash < 0 ? '' : path.slice(0, slash + 1);
  const base = path.slice(slash + 1);
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? { dir, stem: base, ext: '' } : { dir, stem: base.slice(0, dot), ext: base.slice(dot) };
}

/** `path`, or `path` with "-2", "-3", ... before the extension, whichever is not taken. */
export function uniquePath(path: string, taken: ReadonlySet<string>): string {
  if (!taken.has(path)) return path;
  const { dir, stem, ext } = splitExt(path);
  for (let k = 2; ; k++) {
    const candidate = `${dir}${stem}-${String(k)}${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** Every asset path of the document (rename fixes avoid collisions). */
export function assetPaths(doc: Pick<ChartDoc, 'assets'>): Set<string> {
  const out = new Set<string>();
  for (const a of doc.assets.values()) out.add(a.path);
  return out;
}

/**
 * An ASCII-only path for a rename fix: accents are stripped (NFKD), other characters outside [A-Za-z0-9._-/]
 * become "_", runs of "_" collapse, the extension is kept, and the result avoids `taken`.
 */
export function asciiPath(path: string, taken: ReadonlySet<string>): string {
  const clean = (s: string): string =>
    s
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9._-]/g, '_')
      .replace(/_+/g, '_');
  const parts = path.split('/').map((seg, i, all) => {
    let c = clean(seg);
    if (c === '' || /^_*(\.[A-Za-z0-9]*)?$/.test(c)) {
      const { ext } = splitExt(c);
      c = `${i === all.length - 1 ? 'file' : 'dir'}${String(i + 1)}${ext}`;
    }
    return c;
  });
  return uniquePath(parts.join('/'), taken);
}

/** A path that no longer triggers Phira's resource-pack or batch-export detection (PH-05). */
export function safePackagePath(path: string, taken: ReadonlySet<string>): string {
  if (isBatchExportMarker(path)) return uniquePath('export-data.json', taken);
  if (isSkinTriggerName(path)) {
    const stem = path.slice(0, -'click.png'.length);
    return uniquePath(`${stem}click-image.png`, taken);
  }
  return uniquePath(path, taken);
}

// ---------------------------------------------------------------------------------------------------------------
// Easing helpers
// ---------------------------------------------------------------------------------------------------------------

const EXTENT_SAMPLES = 1024;

/** Golden-section refinement of an extreme of f on [a, b] (sign 1: maximum, -1: minimum). */
function refineExtreme(f: (x: number) => number, a: number, b: number, sign: 1 | -1): number {
  const g = (Math.sqrt(5) - 1) / 2;
  let lo = Math.max(0, a);
  let hi = Math.min(1, b);
  let c = hi - g * (hi - lo);
  let d = lo + g * (hi - lo);
  for (let i = 0; i < 50; i++) {
    if (sign * f(c) > sign * f(d)) hi = d;
    else lo = c;
    c = hi - g * (hi - lo);
    d = lo + g * (hi - lo);
  }
  return f((lo + hi) / 2);
}

/**
 * Minimum and maximum eased progress of an ease over x in [0, 1] as Phira evaluates it (hold = 0 everywhere;
 * NaN samples skipped): dense samples, then every interior local extremum refined by golden-section search.
 */
export function easedExtent(e: Ease, samples = EXTENT_SAMPLES): { readonly min: number; readonly max: number } {
  const f = (x: number): number => EaseApi.modelY(e, x);
  const ys: number[] = [];
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i <= samples; i++) {
    const y = f(i / samples);
    ys.push(y);
    if (Number.isNaN(y)) continue;
    if (y < min) min = y;
    if (y > max) max = y;
  }
  for (let i = 1; i < samples; i++) {
    const y = ys[i] ?? Number.NaN;
    const prev = ys[i - 1] ?? Number.NaN;
    const next = ys[i + 1] ?? Number.NaN;
    if (Number.isNaN(y) || Number.isNaN(prev) || Number.isNaN(next)) continue;
    const a = (i - 1) / samples;
    const b = (i + 1) / samples;
    if (y >= prev && y >= next) max = Math.max(max, refineExtreme(f, a, b, 1));
    if (y <= prev && y <= next) min = Math.min(min, refineExtreme(f, a, b, -1));
  }
  return min === Infinity ? { min: 0, max: 0 } : { min, max };
}

/** Families tried, in order, for a non-overshooting replacement of an overshooting family. */
const SAFE_FAMILY: Readonly<Partial<Record<EaseFamily, readonly EaseFamily[]>>> = {
  back: ['cubic', 'quart', 'quad', 'sine'],
  elastic: ['quart', 'cubic', 'quad', 'sine'],
};

/**
 * The nearest easing that stays inside [0, 1]: Back -> Cubic and Elastic -> Quart of the same direction (whole
 * curve); a Bezier gets its y handles clamped into [0, 1] (a cubic Bezier stays inside the hull of its points);
 * anything else that still overshoots (e.g. a slice of Bounce) becomes Linear. Non-overshooting eases are returned
 * unchanged.
 */
export function safeEaseFor(e: Ease): Ease {
  if (!EaseApi.overshoots(e)) return e;
  if (e.kind === 'bezier') {
    const c = (v: number): number => Math.min(1, Math.max(0, v));
    return { kind: 'bezier', p: [c(e.p[0]), c(e.p[1]), c(e.p[2]), c(e.p[3])] };
  }
  if (e.kind === 'tween') {
    const entry = catalogEntry(e.id);
    const families = entry === null ? undefined : SAFE_FAMILY[entry.family];
    if (entry !== null && families !== undefined) {
      for (const fam of families) {
        const v = catalogVariant(fam, entry.variant);
        if (v !== null) return { kind: 'tween', id: v.easingType, left: 0, right: 1 };
      }
    }
  }
  return EaseApi.LINEAR;
}

/** Clamps Bezier x handles into [0, 1] (PH-39); other eases unchanged. */
export function clampBezierHandles(e: Ease): Ease {
  if (e.kind !== 'bezier') return e;
  const c = (v: number): number => Math.min(1, Math.max(0, v));
  return { kind: 'bezier', p: [c(e.p[0]), e.p[1], c(e.p[2]), e.p[3]] };
}

/** Phira's inert rule for a note control (F§12): no points, or two points whose first is easing 1 and value 1. */
export function isInertCurve(c: ControlCurve | null): boolean {
  if (c === null || c.points.length === 0) return true;
  if (c.points.length !== 2) return false;
  const first = c.points[0];
  if (first === undefined) return true;
  return first.easing === 1 && Math.abs(fr(fr(first.value) - 1)) < CONTROL_INERT_EPS;
}

/** True for a curve whose every value is within Phira's tolerance of 1 (looks inert in RPE too). */
export function isNeutralCurve(c: ControlCurve): boolean {
  return c.points.every((p) => Math.abs(fr(fr(p.value) - 1)) < CONTROL_INERT_EPS);
}

// ---------------------------------------------------------------------------------------------------------------
// Text segments (F§9.3, R§3.6)
// ---------------------------------------------------------------------------------------------------------------

export const PERCENT_P = '%P%';

/** Length in Unicode scalar values (Rust `chars().count()`). */
export function charLen(s: string): number {
  let n = 0;
  for (const _ of s) n++;
  return n;
}

/** The string with every `%P%` removed. */
export function stripPercentP(s: string): string {
  return s.split(PERCENT_P).join('');
}

/** Rust's `str::parse::<f32>()` grammar: optional sign, digits with an optional fraction and exponent, inf, nan. */
const RUST_F32_RE = /^[+-]?(?:inf|infinity|nan|(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)$/i;

/** Whether Rust parses the text as an f32 (Phira shows 0 for a `%P%` counter whose remainder does not parse). */
export function parsesAsRustF32(s: string): boolean {
  return RUST_F32_RE.test(s);
}

/**
 * Whether the remainder of a `%P%` counter end (the text without `%P%`) is a number as the author meant it: a plain
 * number, or nothing at all. Phira counts an unparseable remainder as 0 (F§9.3), so the bare "%P%" in "%P%" ->
 * "%P%100" counts up from 0, which is what such a counter means; a label ("Score: %P%0", "100 ") also becomes 0,
 * which is never what the author meant.
 */
export function isCounterNumber(remainder: string): boolean {
  return remainder === '' || parsesAsRustF32(remainder);
}

/** Which case of Phira's string tween a text segment from `x` to `y` takes (F§9.3, in Phira's order). */
export type TextSegmentCase =
  /** Both contain %P%: numeric counter. */
  | 'counter'
  /** Both empty. */
  | 'empty'
  /** `y` empty: typed erase of x. */
  | 'erase'
  /** `x` empty: typed reveal of y. */
  | 'reveal'
  /** `y` starts with `x` (and is longer): typewriter extension. */
  | 'extend'
  /** `x` starts with `y` (and is longer): typewriter shrink. */
  | 'shrink'
  /** Anything else (including x == y): `x` is held until the end key. */
  | 'switch';

export function textSegmentCase(x: string, y: string): TextSegmentCase {
  if (x.includes(PERCENT_P) && y.includes(PERCENT_P)) return 'counter';
  if (x === '' && y === '') return 'empty';
  if (y === '') return 'erase';
  if (x === '') return 'reveal';
  if (x === y) return 'switch';
  if (y.startsWith(x)) return 'extend';
  if (x.startsWith(y)) return 'shrink';
  return 'switch';
}

/** Rust `f32::round` (halves away from zero). */
export function roundHalfAway(v: number): number {
  return v < 0 ? -Math.round(-v) : Math.round(v);
}

/** Tolerance added to the eased extent so a sampled extreme never under-reports Phira's f32 progress. */
const TEXT_EXTENT_SLACK = 1e-4;

/**
 * How many characters Phira's string tween asks for beyond what the string has, at the most extreme eased progress
 * of the segment (0 = never out of range). `reveal`: `round(p·len(y))`; `erase`: `round((1-p)·len(x'))` with x'
 * the start without `%P%`; `extend`: `floor(p·(len y - len x))`; `shrink`: `round((1-p)·(len x - len y))`; all in
 * f32 as Phira computes them. Other cases never index past the end.
 */
export function textSegmentOverflow(
  kind: TextSegmentCase,
  x: string,
  y: string,
  extent: { readonly min: number; readonly max: number },
): number {
  const pMax = fr(extent.max + TEXT_EXTENT_SLACK);
  const oneMinusPMin = fr(1 - (extent.min - TEXT_EXTENT_SLACK));
  switch (kind) {
    case 'reveal': {
      const len = charLen(y);
      return Math.max(0, roundHalfAway(fr(pMax * fr(len))) - len);
    }
    case 'erase': {
      const len = charLen(stripPercentP(x));
      return Math.max(0, roundHalfAway(fr(oneMinusPMin * fr(len))) - len);
    }
    case 'extend': {
      const n = charLen(y) - charLen(x);
      return Math.max(0, Math.floor(fr(fr(n) * pMax)) - n);
    }
    case 'shrink': {
      const n = charLen(x) - charLen(y);
      return Math.max(0, roundHalfAway(fr(fr(n) * oneMinusPMin)) - n);
    }
    case 'counter':
    case 'empty':
    case 'switch':
      return 0;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// extra.json shader uniforms (P§5.2)
// ---------------------------------------------------------------------------------------------------------------

/** GLSL uniform types Phira's default comments accept, and the matching extra.json variable shape. */
export const GLSL_VAR_TYPE: Readonly<Record<string, EffectVarType>> = Object.freeze({
  float: 'float',
  vec2: 'vec2',
  vec4: 'color',
});

export interface UniformSpec {
  readonly name: string;
  readonly type: EffectVarType;
  /** Default from the shader's comment, as an extra.json constant (float, [x, y] or u8 RGBA). */
  readonly value: number | readonly number[];
}

const fu = (name: string, value: number): UniformSpec => ({ name, type: 'float', value });

/** Uniforms of Phira's built-in shaders with their defaults (P§5.2 table; every one has a default comment). */
export const BUILTIN_SHADER_UNIFORMS: Readonly<Record<BuiltinShader, readonly UniformSpec[]>> = Object.freeze({
  chromatic: [fu('sampleCount', 3), fu('power', 0.01)],
  circleBlur: [fu('size', 10)],
  fisheye: [fu('power', -0.1)],
  glitch: [fu('power', 0.03), fu('rate', 0.6), fu('speed', 5), fu('blockCount', 30.5), fu('colorRate', 0.01)],
  grayscale: [fu('factor', 1)],
  noise: [fu('seed', 81), fu('power', 0.03)],
  pixel: [fu('size', 10)],
  radialBlur: [fu('centerX', 0.5), fu('centerY', 0.5), fu('power', 0.01), fu('sampleCount', 6)],
  shockwave: [
    fu('progress', 0.2),
    fu('centerX', 0.5),
    fu('centerY', 0.5),
    fu('width', 0.1),
    fu('distortion', 0.8),
    fu('expand', 10),
  ],
  vignette: [{ name: 'color', type: 'color', value: [0, 0, 0, 255] }, fu('extend', 0.25), fu('radius', 15)],
});

/** One `uniform <type> <name>; // %<default>%` comment of a custom shader. */
export type ShaderDefault =
  | { readonly ok: true; readonly spec: UniformSpec }
  | { readonly ok: false; readonly name: string; readonly glslType: string; readonly reason: 'type' | 'value' };

/** Phira's default-comment pattern (effect.rs; P§5.2). */
const SHADER_DEFAULT_RE = /uniform\s+(\w+)\s+(\w+);\s+\/\/\s+%([^%]+)%/g;

/** Value of a text Rust parses as f32 (see `parsesAsRustF32`), or null when it does not parse. */
export function rustF32Value(text: string): number | null {
  if (!parsesAsRustF32(text)) return null;
  const lower = text.toLowerCase();
  const neg = lower.startsWith('-');
  const body = lower.replace(/^[+-]/, '');
  if (body === 'nan') return Number.NaN;
  if (body === 'inf' || body === 'infinity') return neg ? -Infinity : Infinity;
  return fr(Number(text));
}

function parseFloatList(text: string): number[] | null {
  const out: number[] = [];
  for (const part of text.split(',')) {
    const v = rustF32Value(part.trim());
    if (v === null) return null;
    out.push(v);
  }
  return out;
}

/**
 * The default comments of a custom shader in source order. A type other than float / vec2 / vec4 is a hard error
 * in Phira ("Unknown type"); a default whose numbers do not parse (or have the wrong count) fails the same way.
 */
export function parseShaderDefaults(src: string): readonly ShaderDefault[] {
  const out: ShaderDefault[] = [];
  SHADER_DEFAULT_RE.lastIndex = 0;
  for (let m = SHADER_DEFAULT_RE.exec(src); m !== null; m = SHADER_DEFAULT_RE.exec(src)) {
    const glslType = m[1] ?? '';
    const name = m[2] ?? '';
    const text = m[3] ?? '';
    const type = GLSL_VAR_TYPE[glslType];
    if (type === undefined) {
      out.push({ ok: false, name, glslType, reason: 'type' });
      continue;
    }
    const parsed = parseFloatList(text);
    const want = type === 'float' ? 1 : type === 'vec2' ? 2 : 4;
    if (parsed === null || parsed.length !== want) {
      out.push({ ok: false, name, glslType, reason: 'value' });
      continue;
    }
    // inf / nan parse in Phira; the extra.json constant offered by a fix must be finite.
    const nums = parsed.map((v) => (Number.isFinite(v) ? v : 0));
    const value: number | readonly number[] =
      type === 'float'
        ? (nums[0] ?? 0)
        : type === 'vec2'
          ? nums
          : nums.map((v) => Math.min(255, Math.max(0, Math.round(v * 255))));
    out.push({ ok: true, spec: { name, type, value } });
  }
  return out;
}

/**
 * The optional runtime members of the CheckContext (contract since CR-W2-12b-2 / CR-W2-12b-5): custom shader texts
 * for PH-07 and the preview's shader compile errors for PH-33 (absent maps make those parts of the rules silent), and
 * the music asset the decoded song length belongs to (see `musicLengthSec`; absent: the runtime does not track it,
 * and the decoded length is trusted only when it agrees with the asset's).
 */
export type CheckContextExtensions = Pick<CheckContext, 'shaderSources' | 'shaderErrors' | 'songLengthAsset'>;

export function contextExtensions(ctx: CheckContext): CheckContextExtensions {
  return ctx;
}

// ---------------------------------------------------------------------------------------------------------------
// Bezier registry scan (F§6.7, PH-20)
// ---------------------------------------------------------------------------------------------------------------

export interface BezierUse {
  /** Phira's 0.01-quantised cache key (`Ease.bezierKey`). */
  readonly key: string;
  /** Points as Phira stores them (f32). */
  readonly points: BezierPoints;
  readonly line: LineId | null;
  /** Export index of the line. */
  readonly lineIndex: number;
  /** JSON list name, e.g. 'moveXEvents'. */
  readonly list: string;
  /** Key whose segment produced the event (null for padding and for raw lists Phira ignores for rendering). */
  readonly srcKey: KeyId | null;
}

function f32Points(p: BezierPoints): BezierPoints {
  return [fr(p[0]), fr(p[1]), fr(p[2]), fr(p[3])];
}

/** Every Bezier event of a lowered chart in Phira's registry scan order (speed lists are never scanned). */
export function scanBezierUses(lines: readonly IrLine[]): BezierUse[] {
  const out: BezierUse[] = [];
  lines.forEach((line, lineIndex) => {
    const push = (points: BezierPoints, list: string, srcKey: KeyId | null): void => {
      out.push({
        key: EaseApi.bezierKey(points),
        points: f32Points(points),
        line: line.srcLine,
        lineIndex,
        list,
        srcKey,
      });
    };
    for (const layer of line.eventLayers) {
      if (layer === null) continue;
      for (const list of LAYER_BEZIER_SCAN) {
        for (const ev of layer[list] ?? []) if (ev.bezier !== 0) push(ev.bezierPoints, list, ev.srcKey);
      }
    }
    const ext = extendedOf(line);
    if (ext === null) return;
    for (const list of EXTENDED_BEZIER_SCAN) {
      for (const ev of ext[list] ?? []) if (ev.bezier !== 0) push(ev.bezierPoints, list, ev.srcKey);
      for (const raw of ignoredList(ext, list) ?? []) {
        const p = rawEventBezier(raw);
        if (p !== null) push(p, list, null);
      }
    }
  });
  return out;
}

/** True when two f32 point sets are identical. */
export function samePoints(a: BezierPoints, b: BezierPoints): boolean {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

/**
 * The curve moved by the smallest y-handle step (0.01, then 0.02 ...) whose cache key is not in `taken`: it keeps
 * its shape within 0.01 and gets a registry entry of its own. Handles inside [0, 1] stay inside. null when no step
 * up to 0.05 finds a free key.
 */
export function nudgeBezier(p: BezierPoints, taken: ReadonlySet<string>): BezierPoints | null {
  const inUnit = (v: number): boolean => v >= 0 && v <= 1;
  for (let k = 1; k <= 5; k++) {
    for (const sign of [1, -1]) {
      for (const which of [3, 1] as const) {
        const q: [number, number, number, number] = [p[0], p[1], p[2], p[3]];
        const v = Math.round((q[which] + sign * k * 0.01) * 1000) / 1000;
        if (inUnit(q[which]) && !inUnit(v)) continue;
        q[which] = v;
        if (!taken.has(EaseApi.bezierKey(q))) return q;
      }
    }
  }
  return null;
}
